import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import tippy, { Instance, Props } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { Bold, Italic, Strikethrough, List, ListOrdered, Quote, Undo, Redo } from 'lucide-react';
import { User } from '../types';

interface RichCommentEditorProps {
  value: string;
  onChange: (rawText: string, html: string) => void;
  onSearchUsers: (query: string) => Promise<User[]>;
  users?: User[]; // optional list for initial suggestions
}

const RichCommentEditor: React.FC<RichCommentEditorProps> = ({ value, onChange, onSearchUsers, users = [] }) => {
  const [mentionQuery, setMentionQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        renderText({ node }) { return `@${node.attrs.label ?? node.attrs.id}`; },
        // Keep TipTap's DOM as a span with data attributes for downstream parsing
        renderHTML({ node }) {
          const label = `@${node.attrs.label ?? node.attrs.id}`;
          return ['span', { 'data-type': 'mention', 'data-id': node.attrs.id, 'data-label': node.attrs.label, class: 'mention' }, label];
        },
        suggestion: {
          char: '@',
          items: async ({ query }) => {
            setMentionQuery(query);
            if (!query) {
              if (!users || users.length === 0) {
                setMentionLoading(true);
                const fetchedAll = await onSearchUsers('');
                setFilteredUsers(fetchedAll);
                setMentionLoading(false);
                return fetchedAll.map(u => ({ id: u.id, label: u.name, email: u.email }));
              }
              setFilteredUsers(users.slice(0, 8));
              return users.slice(0, 8).map(u => ({ id: u.id, label: u.name, email: u.email }));
            }
            setMentionLoading(true);
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
            return new Promise((resolve) => {
              debounceTimeout.current = setTimeout(async () => {
                const fetchedUsers = await onSearchUsers(query);
                setFilteredUsers(fetchedUsers);
                setMentionLoading(false);
                resolve(fetchedUsers.map(u => ({ id: u.id, label: u.name, email: u.email })));
              }, 250);
            });
          },
          render: () => {
            let component: HTMLDivElement;
            let tippyInstance: Instance<Props>;
            let selectedIndex = 0;
            let items: Array<{ id:number; label:string; email:string }> = [];
            let command: (attrs: any) => void = () => {};
            let searchInput: HTMLInputElement;
            let localDebounce: number | null = null;

            const renderItems = () => {
              if (!component) return;
              const list = component.querySelector('[data-list]') as HTMLDivElement;
              if (!list) return;
              list.innerHTML = '';
              items.forEach((item, index) => {
                const div = document.createElement('div');
                div.className = `px-3 py-1.5 text-sm cursor-pointer ${index === selectedIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`;
                div.textContent = `${item.label} (${item.email})`;
                div.addEventListener('mousedown', (e) => {
                  e.preventDefault();
                  command({ id: item.id, label: item.label });
                });
                list.appendChild(div);
              });
            };

            const updateByQuery = async (q: string) => {
              const result = await onSearchUsers(q);
              items = result.map(u => ({ id: u.id, label: u.name, email: u.email }));
              selectedIndex = 0;
              renderItems();
            };

            return {
              onStart: (props) => {
                command = props.command;
                items = props.items as any;
                selectedIndex = 0;

                component = document.createElement('div');
                component.className = 'glass border border-blue-200 rounded-lg shadow-lg bg-white min-w-[280px]';
                component.style.maxHeight = '320px';
                component.style.overflow = 'hidden';

                // Header with input
                const header = document.createElement('div');
                header.className = 'p-2 border-b border-blue-100 bg-white/80';
                searchInput = document.createElement('input');
                searchInput.type = 'text';
                searchInput.placeholder = 'Search users…';
                searchInput.className = 'w-full text-sm px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200';
                header.appendChild(searchInput);
                component.appendChild(header);

                const list = document.createElement('div');
                list.setAttribute('data-list', '');
                list.className = 'py-1 overflow-y-auto max-h-[260px]';
                component.appendChild(list);

                // initial render
                renderItems();

                // typing in the box filters users
                searchInput.addEventListener('input', (e) => {
                  e.stopPropagation();
                  const q = (e.target as HTMLInputElement).value;
                  if (localDebounce) window.clearTimeout(localDebounce);
                  localDebounce = window.setTimeout(() => updateByQuery(q), 250);
                });
                searchInput.addEventListener('keydown', (e) => e.stopPropagation());

                tippyInstance = tippy(document.body as any, {
                  getReferenceClientRect: props.clientRect as any,
                  appendTo: () => document.body,
                  content: component,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                }) as unknown as Instance<Props>;

                // autofocus the input when popup opens
                setTimeout(() => searchInput?.focus(), 0);
              },
              onUpdate(props) {
                items = props.items as any;
                selectedIndex = 0;
                renderItems();
                tippyInstance.setProps({ getReferenceClientRect: props.clientRect as any });
              },
              onKeyDown(props) {
                if (props.event.key === 'ArrowDown') {
                  selectedIndex = (selectedIndex + 1) % items.length;
                  renderItems();
                  return true;
                }
                if (props.event.key === 'ArrowUp') {
                  selectedIndex = (selectedIndex - 1 + items.length) % items.length;
                  renderItems();
                  return true;
                }
                if (props.event.key === 'Enter') {
                  const item = items[selectedIndex];
                  if (item) command({ id: item.id, label: item.label });
                  return true;
                }
                return false;
              },
              onExit() {
                if (tippyInstance) tippyInstance.destroy();
              },
            };
          },
        },
      }),
    ],
    content: value,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      const raw = editor.getText();
      onChange(raw, html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[100px] p-3 bg-white/60 backdrop-blur-sm rounded-xl',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  const MenuButton: React.FC<{ editor: any; command: string; icon: React.ElementType }> = ({ editor, command, icon: Icon }) => (
    <button
      onClick={() => editor.chain().focus()[command]().run()}
      className={`p-1.5 rounded-md transition-colors duration-200 ${editor.isActive(command) ? 'bg-blue-200 text-blue-800' : 'text-gray-600 hover:bg-gray-100'}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border-2 border-blue-100 rounded-xl shadow-sm focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all duration-200">
      {editor && (
        <div className="flex flex-wrap gap-1 p-2 border-b border-blue-100 bg-white/70 rounded-t-xl">
          <MenuButton editor={editor} command="toggleBold" icon={Bold} />
          <MenuButton editor={editor} command="toggleItalic" icon={Italic} />
          <MenuButton editor={editor} command="toggleStrike" icon={Strikethrough} />
          <MenuButton editor={editor} command="toggleBulletList" icon={List} />
          <MenuButton editor={editor} command="toggleOrderedList" icon={ListOrdered} />
          <MenuButton editor={editor} command="toggleBlockquote" icon={Quote} />
          <MenuButton editor={editor} command="undo" icon={Undo} />
          <MenuButton editor={editor} command="redo" icon={Redo} />
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichCommentEditor;
